import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../api';
import type { VideoAnalytics } from '../types';
import { formatDuration } from '../utils';
import { Icon } from './Icon';

export function playbackOriginLabel(origin: string): string {
  if (origin === 'public_share') return 'Public share page';
  if (origin === 'unknown') return 'Direct or privacy-limited';
  return origin;
}

export function VideoAnalyticsPanel({ videoId }: { videoId: string }) {
  const [range, setRange] = useState(30);
  const [analytics, setAnalytics] = useState<VideoAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.videoAnalytics(videoId, range).then((value) => active && setAnalytics(value))
      .catch((reason) => active && setError(reason instanceof ApiError ? reason.message : 'Unable to load playback analytics.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [range, videoId]);
  const maximumViews = useMemo(() => Math.max(1, ...(analytics?.daily.map((day) => day.views) ?? [])), [analytics]);
  const maximumRetention = useMemo(() => Math.max(1, ...(analytics?.retention.map((item) => item.viewers) ?? [])), [analytics]);
  return <div className="analytics-panel">
    <div className="analytics-panel__toolbar"><p>First-party player events only. A view counts after five active seconds; raw events expire after 30 days.</p><select className="control-select" aria-label="Analytics date range" value={range} onChange={(event) => setRange(Number(event.target.value))}><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option></select></div>
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    {loading ? <div className="video-details__links-loading"><span className="spinner spinner--large" />Loading statistics…</div> : analytics && <>
      <section className="analytics-metrics">
        <article><span>Qualified views</span><strong>{analytics.summary.views.toLocaleString()}</strong><small>One per playback session</small></article>
        <article><span>Total watch time</span><strong>{formatDuration(analytics.summary.watchSeconds)}</strong><small>Active foreground playback</small></article>
        <article><span>Average watch time</span><strong>{formatDuration(analytics.summary.averageWatchSeconds)}</strong><small>Watch time ÷ views</small></article>
        <article><span>Completion rate</span><strong>{Math.round(analytics.summary.completionRate * 100)}%</strong><small>{analytics.summary.completions.toLocaleString()} completed plays</small></article>
      </section>
      <section className="video-details__card analytics-chart">
        <div className="video-details__section-heading"><div><span>Trend</span><h3>Views by day</h3></div></div>
        {analytics.daily.length ? <div className="analytics-bars">{analytics.daily.map((day) => <div key={day.day}><span title={`${day.views} views`} style={{ height: `${Math.max(4, day.views / maximumViews * 100)}%` }} /><small>{new Date(`${day.day}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</small></div>)}</div> : <EmptyAnalytics />}
      </section>
      <section className="video-details__card analytics-chart">
        <div className="video-details__section-heading"><div><span>Engagement</span><h3>Viewer retention</h3></div></div>
        <p className="analytics-chart__description">Viewing sessions recorded at each 5% point in the video.</p>
        {analytics.retention.length ? <div className="retention-bars">{analytics.retention.map((item) => <div key={item.percent}><span>{item.percent}%</span><i><b style={{ width: `${item.viewers / maximumRetention * 100}%` }} /></i><strong>{item.viewers}</strong></div>)}</div> : <EmptyAnalytics />}
      </section>
      <section className="video-details__card analytics-origins">
        <div className="video-details__section-heading"><div><span>Placement</span><h3>Playback origins</h3></div></div>
        {analytics.origins.length ? analytics.origins.map((item) => <div key={item.origin}><span className="analytics-origin">{playbackOriginLabel(item.origin)}</span><span>{item.views.toLocaleString()} views</span><span>{formatDuration(item.watchSeconds)}</span></div>) : <EmptyAnalytics />}
      </section>
    </>}
  </div>;
}

function EmptyAnalytics() {
  return <div className="analytics-empty"><Icon name="film" size={22} /><span>No playback data in this period.</span></div>;
}
